import {
  useCallback, useEffect, useLayoutEffect, useRef,
} from 'react';
import { REQUEST_OUTCOMES, requestTransport } from '../../../../actions/helpers/requestTransport';
import { findMissingReferences, referencedValues } from './savedViewFilters';

// ENG-3551: before a view is applied, re-read every reference list the view uses
// (labels, teams, categories, …) so a value deleted since the view was saved
// blocks the apply instead of silently widening the list. The reads go straight
// through requestTransport — NOT the Redux actions — so validation never changes
// the `labels` / `teams` / `userProfile` slices the page is rendering, and a
// failing list can never throw inside a reducer.

const listFrom = (data, key, toOption) => {
  const rows = data?.[key];
  if (!Array.isArray(rows)) return null;
  const values = [];
  for (let index = 0; index < rows.length; index += 1) {
    const option = toOption(rows[index]);
    if (!option) return null;
    values.push(option);
  }
  return values;
};

const option = (value, label) => (
  value === undefined || value === null ? null : { value: String(value), label: String(label ?? value) }
);

// Same endpoints the page's existing loaders call; `parse` returns null for an
// unexpected body shape (treated as unverifiable, never as "nothing exists").
export const SAVED_VIEW_REFERENCE_LOADERS = {
  tag: {
    url: '/api/v1/labels?sort=label_usage&sort_dir=DESC&page_size=max',
    parse: (data) => listFrom(data, 'labels', (row) => option(row?.id, row?.name)),
  },
  team: {
    url: '/api/v1/teams',
    parse: (data) => listFrom(data, 'teams', (row) => option(row?.id, row?.name)),
  },
  category: {
    url: '/api/v1/document_categories?no_pagination=true',
    parse: (data) => listFrom(data, 'categories', (row) => option(row?.emr_category, row?.emr_category)),
  },
  referral_statuses: {
    url: '/api/v1/referral_statuses?no_pagination=true',
    parse: (data) => listFrom(data, 'referral_statuses', (row) => option(row?.name, row?.name)),
  },
  assignee: {
    url: '/api/v1/department_users?page_size=10000',
    parse: (data) => listFrom(data, 'users', (row) => (
      Array.isArray(row) ? option(row[0], row[1] || row[0]) : null
    )),
  },
};

const isSuccessStatus = (status) => typeof status === 'number' && status >= 200 && status < 300;

export const loadReferenceList = async (key) => {
  const loader = SAVED_VIEW_REFERENCE_LOADERS[key];
  if (!loader) return { status: 'failed', values: [] };
  try {
    const result = await requestTransport({ method: 'GET', url: loader.url }, { retryOnUnavailable: true });
    if (result?.outcome !== REQUEST_OUTCOMES.success) return { status: 'failed', values: [] };
    if (!isSuccessStatus(result.value?.status)) return { status: 'failed', values: [] };
    const values = loader.parse(result.value?.data);
    if (!values) return { status: 'failed', values: [] };
    return { status: 'ready', values };
  } catch {
    return { status: 'failed', values: [] };
  }
};

// Departments: the department filter matches slugs, and `/api/v1/departments`
// does not serialize them, so the authoritative list is the one the department
// picker itself renders (`state.auth.departments`, slug + name).
const departmentOption = (departments) => {
  if (!Array.isArray(departments)) return { status: 'failed', values: [] };
  return {
    status: 'ready',
    values: departments
      .filter((department) => department && department.slug != null)
      .map((department) => ({ value: String(department.slug), label: String(department.name ?? department.slug) })),
  };
};

const labelsFrom = (values = []) => values.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

const STALE_CHECK = Object.freeze({
  missing: [], unverifiable: [], options: {}, stale: true,
});

const withKnownLabels = (entry, known) => ({ ...entry, knownLabels: { ...known, ...labelsFrom(entry.values) } });

export const useSavedViewOptions = ({
  page, enabled, orgId, departments = [], knownLabels = {},
}) => {
  const generationRef = useRef(0);
  const knownLabelsRef = useRef({});
  const departmentsRef = useRef(departments);
  const seededLabelsRef = useRef(knownLabels);
  useLayoutEffect(() => {
    departmentsRef.current = departments;
    seededLabelsRef.current = knownLabels;
  });

  // An org switch (or unmount) obsoletes every in-flight check.
  useEffect(() => {
    generationRef.current += 1;
    knownLabelsRef.current = {};
    return () => { generationRef.current += 1; };
  }, [orgId, page, enabled]);

  const reset = useCallback(() => {
    generationRef.current += 1;
  }, []);

  // Resolves { missing, unverifiable, options, stale }. `stale` means a newer
  // check, reset, org change or unmount superseded this one: callers must ignore it.
  const check = useCallback(async (filters) => {
    if (!enabled) return STALE_CHECK;
    generationRef.current += 1;
    const generation = generationRef.current;
    const needed = Object.keys(referencedValues(filters, page));

    const entries = await Promise.all(needed.map(async (key) => {
      if (key === 'department') return [key, departmentOption(departmentsRef.current)];
      return [key, await loadReferenceList(key)];
    }));

    if (generation !== generationRef.current) return STALE_CHECK;

    const options = {};
    entries.forEach(([key, entry]) => {
      const known = { ...(seededLabelsRef.current?.[key] || {}), ...(knownLabelsRef.current[key] || {}) };
      options[key] = withKnownLabels(entry, known);
      if (entry.status === 'ready') {
        knownLabelsRef.current[key] = { ...(knownLabelsRef.current[key] || {}), ...labelsFrom(entry.values) };
      }
    });
    const { missing, unverifiable } = findMissingReferences(filters, page, options);
    return {
      missing, unverifiable, options, stale: false,
    };
  }, [enabled, page]);

  return { check, reset };
};

export default useSavedViewOptions;
